/**
 * Puts the PC's runner (deviceRunner.ts) on the page: the bridge it asks, the
 * Worker it publishes to, the turn store it takes replies on through, and the
 * hub's `wake`, `cancel`, `pair` and `approval`. Only Assist's own window has
 * a PC to be; in any other page `pc` is null at once and nothing here runs.
 *
 * `pc` is what the hub opens as: undefined until the window has published
 * (the hub waits for it, since the Worker wants the PC published first), then
 * its id, or null for a window that is not a PC or whose publish has not
 * gone through yet. `refused` is the Worker's reason when it will not have
 * this PC at all.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { HubServerFrame } from '@wire';
import { answerPairing, getChat, putDevice, reportDevice } from '../api';
import {
  deliverApproval,
  deliverPairing,
  desktopDevice,
  desktopTools,
  inDesktop,
  noteFromWake,
  onDesktopApprovalRequest,
  onDesktopAsking,
  onDesktopChange,
  tellDesktopChatStopped,
  tellDesktopDispatched,
  tellDesktopReplyEnded
} from '../desktop';
import { DeviceRunner } from '../deviceRunner';
import { toSummary } from './chatStash';
import { abandonTurn, peekTurn, takeOn, watchTurn, type TurnApp } from './useTurns';

export function useDeviceRunner(
  allowed: boolean,
  app: TurnApp
): { pc: string | null | undefined; refused: string | null; frame: (frame: HubServerFrame) => void } {
  const [pc, setPc] = useState<string | null | undefined>(() => (inDesktop() ? undefined : null));
  const [refused, setRefused] = useState<string | null>(null);
  const runner = useRef<DeviceRunner | null>(null);
  const turnApp = useRef(app);
  turnApp.current = app;

  useEffect(() => {
    if (!allowed || !inDesktop()) return;
    const r = new DeviceRunner({
      info: desktopDevice,
      tools: desktopTools,
      put: putDevice,
      getChat,
      report: reportDevice,
      takeOn: (chat, message, again) => takeOn({ chat: toSummary(chat), message, app: turnApp.current, again }),
      turn: peekTurn,
      watch: watchTurn,
      abandon: (chatId, messageId) => void abandonTurn(chatId, messageId),
      ended: (messageId) => void tellDesktopReplyEnded(messageId),
      stopped: (chatId) => void tellDesktopChatStopped(chatId),
      dispatched: (messageId) => void tellDesktopDispatched(messageId),
      fromWake: noteFromWake,
      approve: deliverApproval,
      pair: deliverPairing,
      answerPairing: (id, phone, ok) => answerPairing(id, phone, { ok }),
      refused: setRefused
    });
    runner.current = r;
    void r.start(setPc);
    const offTools = onDesktopChange(() => r.toolsChanged());
    const offAsking = onDesktopAsking((runId, on) => r.asking(runId, on));
    const offRequests = onDesktopApprovalRequest((json) => r.approvalRequest(json));
    return () => {
      r.stop();
      offTools();
      offAsking();
      offRequests();
      runner.current = null;
    };
  }, [allowed]);

  const frame = useCallback((f: HubServerFrame) => {
    const r = runner.current;
    if (!r) return;
    if (f.t === 'wake') void r.wake(f);
    else if (f.t === 'cancel') r.cancel(f);
    else if (f.t === 'dispatch') r.dispatch(f.chat);
    else if (f.t === 'approval') r.approval(f);
    else if (f.t === 'pair') void r.pair(f);
    else if (f.t === 'device_gone') r.gone(f.deviceId);
  }, []);

  return { pc, refused, frame };
}
